import apiClient from "./apiClient";
import Fuse from "fuse.js";
import type {
  SilaeVariable,
  SilaeVariableCategory,
  CreateSilaeVariableInput,
  UpdateSilaeVariableInput,
  GetSilaeVariablesFilters,
  SilaeVariableApiResponse,
  SingleSilaeVariableApiResponse,
  VariableCountByCategory,
  VariableStatsApiResponse,
} from "@/types/silae";

class SilaeVariableService {
  private baseURL = "/api/silae-variables";

  // Get all variables with optional filters
  async getSilaeVariables(
    filters: GetSilaeVariablesFilters = {},
  ): Promise<SilaeVariable[]> {
    try {
      const params = new URLSearchParams();

      if (filters.category) params.append("category", filters.category);
      if (filters.type) params.append("type", filters.type);
      if (filters.isActive !== undefined)
        params.append("isActive", filters.isActive.toString());
      if (filters.isFrequentlyUsed !== undefined)
        params.append("isFrequentlyUsed", filters.isFrequentlyUsed.toString());
      if (filters.search) params.append("search", filters.search);

      const queryString = params.toString();
      const url = queryString ? `${this.baseURL}?${queryString}` : this.baseURL;

      const response = await apiClient.get<SilaeVariableApiResponse>(url);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching SILAE variables:", error);
      throw error;
    }
  }

  // Get variable by ID
  async getSilaeVariableById(id: string): Promise<SilaeVariable> {
    try {
      const response = await apiClient.get<SingleSilaeVariableApiResponse>(
        `${this.baseURL}/${id}`,
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching SILAE variable ${id}:`, error);
      throw error;
    }
  }

  // Get variable by code
  async getSilaeVariableByCode(code: string): Promise<SilaeVariable> {
    try {
      const response = await apiClient.get<SingleSilaeVariableApiResponse>(
        `${this.baseURL}/code/${code}`,
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching SILAE variable with code ${code}:`, error);
      throw error;
    }
  }

  // Create new variable
  async createSilaeVariable(
    input: CreateSilaeVariableInput,
  ): Promise<SilaeVariable> {
    try {
      const response = await apiClient.post<SingleSilaeVariableApiResponse>(
        this.baseURL,
        input,
      );
      return response.data.data;
    } catch (error) {
      console.error("Error creating SILAE variable:", error);
      throw error;
    }
  }

  // Update variable
  async updateSilaeVariable(
    id: string,
    input: UpdateSilaeVariableInput,
  ): Promise<SilaeVariable> {
    try {
      const response = await apiClient.put<SingleSilaeVariableApiResponse>(
        `${this.baseURL}/${id}`,
        input,
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error updating SILAE variable ${id}:`, error);
      throw error;
    }
  }

  // Delete variable
  async deleteSilaeVariable(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.baseURL}/${id}`);
    } catch (error) {
      console.error(`Error deleting SILAE variable ${id}:`, error);
      throw error;
    }
  }

  // Get variables by category
  async getSilaeVariablesByCategory(
    category: SilaeVariableCategory,
  ): Promise<SilaeVariable[]> {
    try {
      const response = await apiClient.get<SilaeVariableApiResponse>(
        `${this.baseURL}/category/${category}`,
      );
      return response.data.data;
    } catch (error) {
      console.error(
        `Error fetching SILAE variables for category ${category}:`,
        error,
      );
      throw error;
    }
  }

  // Get frequently used variables
  async getFrequentlyUsedSilaeVariables(): Promise<SilaeVariable[]> {
    try {
      const response = await apiClient.get<SilaeVariableApiResponse>(
        `${this.baseURL}/frequently-used`,
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching frequently used SILAE variables:", error);
      throw error;
    }
  }

  // Search variables
  async searchSilaeVariables(searchTerm: string): Promise<SilaeVariable[]> {
    try {
      const response = await apiClient.get<SilaeVariableApiResponse>(
        `${this.baseURL}/search?q=${encodeURIComponent(searchTerm)}`,
      );
      return response.data.data;
    } catch (error) {
      console.error(
        `Error searching SILAE variables with term "${searchTerm}":`,
        error,
      );
      throw error;
    }
  }

  // Smart search with Fuse.js fuzzy search
  async searchSilaeVariablesWithPriority(
    searchTerm: string,
  ): Promise<SilaeVariable[]> {
    try {
      // Get ALL variables (not just search results) to ensure we don't miss exact code matches
      const allVariablesResponse =
        await apiClient.get<SilaeVariableApiResponse>(
          `${this.baseURL}?isActive=true`,
        );
      const allVariables = allVariablesResponse.data.data;

      // Configure Fuse.js with settings optimized for SILAE variable codes
      const fuseOptions = {
        keys: [
          {
            name: "code",
            weight: 0.9, // Extremely high priority for code matches
          },
          {
            name: "definition",
            weight: 0.8, //  medium priority for definition matches
          },
        ],
        threshold: 0.6, // Allow more fuzzy matching for typos
        distance: 100, // Allow longer distance for fuzzy matching
        includeScore: true,
        ignoreLocation: true,
        findAllMatches: true,
        minMatchCharLength: 1,
        shouldSort: true,
        useExtendedSearch: false,
      };

      const fuse = new Fuse(allVariables, fuseOptions);
      const fuseResults = fuse.search(searchTerm);

      // Custom scoring to prioritize exact code matches and code contains
      const scoredResults = fuseResults.map((result) => {
        const variable = result.item;
        const code = variable.code.toLowerCase();
        const term = searchTerm.toLowerCase();
        let customScore = result.score || 1;

        // Boost exact code matches
        if (code === term) {
          customScore = 0.001; // Best possible score
        }
        // Boost code starts with term
        else if (code.startsWith(term)) {
          customScore = 0.01;
        }
        // Boost code contains term after dot (e.g., "BUL.AGE" for "age")
        else if (code.includes("." + term)) {
          customScore = 0.02;
        }
        // Boost any code contains
        else if (code.includes(term)) {
          customScore = 0.05;
        }
        // Boost frequently used variables
        if (variable.isFrequentlyUsed) {
          customScore *= 0.8;
        }

        return {
          item: variable,
          score: customScore,
        };
      });

      // Sort by custom score and return top 4
      scoredResults.sort((a, b) => a.score - b.score);
      return scoredResults.slice(0, 4).map((result) => result.item);
    } catch (error) {
      console.error(
        `Error searching SILAE variables with Fuse.js for term "${searchTerm}":`,
        error,
      );
      throw error;
    }
  }

  // Toggle variable active status
  async toggleSilaeVariableActive(id: string): Promise<SilaeVariable> {
    try {
      const response = await apiClient.patch<SingleSilaeVariableApiResponse>(
        `${this.baseURL}/${id}/toggle-active`,
      );
      return response.data.data;
    } catch (error) {
      console.error(
        `Error toggling SILAE variable ${id} active status:`,
        error,
      );
      throw error;
    }
  }

  // Get variable count by category (for stats)
  async getVariableCountByCategory(): Promise<VariableCountByCategory> {
    try {
      const response = await apiClient.get<VariableStatsApiResponse>(
        `${this.baseURL}/stats/by-category`,
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching variable count by category:", error);
      throw error;
    }
  }

  // Bulk create variables (for admin import)
  async bulkCreateSilaeVariables(
    variables: CreateSilaeVariableInput[],
  ): Promise<SilaeVariable[]> {
    try {
      const response = await apiClient.post<SilaeVariableApiResponse>(
        `${this.baseURL}/bulk`,
        {
          variables,
        },
      );
      return response.data.data;
    } catch (error) {
      console.error("Error bulk creating SILAE variables:", error);
      throw error;
    }
  }
}

export const silaeVariableService = new SilaeVariableService();
export default silaeVariableService;
